import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";

const CURRENCIES = ["usd", "eur", "gbp", "inr", "aed", "sgd"];
const INTERVALS = [
  { v: "one_time", label: "One-time" },
  { v: "month", label: "Monthly" },
  { v: "year", label: "Yearly" },
];

export default function CreatePaymentLink() {
  const navigate = useNavigate();
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [amount, setAmount] = useState("");
  const [currency, setCurrency] = useState("usd");
  const [interval, setInterval] = useState("one_time");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    const parsed = parseFloat(amount);
    if (!parsed || parsed <= 0) { toast.error("Enter a valid amount"); return; }
    setLoading(true);
    try {
      await api.post("/payment-links", { title, description, amount: parsed, currency, interval });
      toast.success("Payment link created");
      navigate(`/merchant/links`);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    } finally { setLoading(false); }
  };

  return (
    <main data-testid="create-link-page" className="max-w-3xl mx-auto px-6 lg:px-10 py-16">
      <div className="text-xs uppercase tracking-[0.3em] text-gold">Atelier</div>
      <h1 className="font-display text-5xl tracking-tighter mt-3">Compose a payment.</h1>
      <p className="text-secondary-fg mt-3 max-w-md">Shape an immutable, shareable link. Anyone with the address can pay; only you receive.</p>

      <form onSubmit={submit} className="mt-16 space-y-10">
        <div>
          <label className="text-xs uppercase tracking-[0.25em] text-muted-fg">Title</label>
          <input data-testid="link-title" required value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Studio session — March" className="lux-input mt-2" />
        </div>
        <div>
          <label className="text-xs uppercase tracking-[0.25em] text-muted-fg">Description (optional)</label>
          <input data-testid="link-description" value={description} onChange={(e) => setDescription(e.target.value)} placeholder="One-time consultation" className="lux-input mt-2" />
        </div>
        <div className="grid grid-cols-12 gap-6">
          <div className="col-span-8">
            <label className="text-xs uppercase tracking-[0.25em] text-muted-fg">Amount</label>
            <input data-testid="link-amount" type="number" min="0.50" step="0.01" required value={amount} onChange={(e) => setAmount(e.target.value)} placeholder="0.00" className="lux-input mt-2 font-display text-3xl" />
          </div>
          <div className="col-span-4">
            <label className="text-xs uppercase tracking-[0.25em] text-muted-fg">Currency</label>
            <select data-testid="link-currency" value={currency} onChange={(e) => setCurrency(e.target.value)} className="lux-input mt-2 uppercase">
              {CURRENCIES.map((c) => <option key={c} value={c} className="bg-bg">{c.toUpperCase()}</option>)}
            </select>
          </div>
        </div>

        <div>
          <label className="text-xs uppercase tracking-[0.25em] text-muted-fg">Billing</label>
          <div className="grid grid-cols-3 gap-3 mt-3">
            {INTERVALS.map((i) => (
              <button
                key={i.v}
                type="button"
                data-testid={`interval-${i.v}`}
                onClick={() => setInterval(i.v)}
                className={`py-3 border text-sm transition-all ${interval === i.v ? "border-gold text-gold bg-[rgba(212,175,55,0.05)]" : "hairline text-secondary-fg hover:text-white"}`}
              >
                {i.label}
              </button>
            ))}
          </div>
          {interval !== "one_time" && (
            <p className="text-xs text-muted-fg mt-3">Customers will be tracked as subscribers; manual billing for each cycle in v1.</p>
          )}
        </div>

        <button data-testid="link-submit" type="submit" disabled={loading} className="btn-gold w-full md:w-auto">
          {loading ? "Composing…" : "Create payment link"}
        </button>
      </form>
    </main>
  );
}
